import { useEffect, useState } from 'react';
import { Users, Globe, MessageSquare, TrendingUp, ArrowRight, Activity } from 'lucide-react';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

export default function GuestAnalyticsCard({ colors, isDark }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/guest-analytics?days=${days}`, { withCredentials: true })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  const cardBg = isDark ? 'rgba(30,41,59,0.5)' : 'rgba(241,245,249,0.8)';
  const statBg = isDark ? 'rgba(15,23,42,0.6)' : 'rgba(255,255,255,0.8)';

  if (loading && !data) {
    return (
      <div className="rounded-2xl p-6 animate-pulse" style={{ background: cardBg }}>
        <div className="h-5 w-40 rounded bg-slate-700/30 mb-4" />
        <div className="grid grid-cols-3 gap-3">
          {[1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-slate-700/20" />)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="rounded-2xl p-6" style={{ background: cardBg }} data-testid="guest-analytics-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" style={{ color: colors.accent }} />
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Guest Analytics</h3>
        </div>
        <div className="flex gap-1 rounded-lg p-0.5" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#e2e8f0' }}>
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className="px-2.5 py-1 rounded-md text-[10px] font-medium transition-all"
              style={days === d ? { background: 'rgba(99,102,241,0.2)', color: '#818cf8' } : { color: colors.textMuted }}
              data-testid={`guest-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        {[
          { label: 'Unique Guests', value: data.unique_guests, icon: Users, color: '#818cf8' },
          { label: 'Active (Messaged)', value: data.active_guests, icon: Activity, color: '#34d399' },
          { label: 'Total Messages', value: data.total_messages, icon: MessageSquare, color: '#60a5fa' },
          { label: 'Avg Msgs/Guest', value: data.avg_messages_per_guest, icon: TrendingUp, color: '#fbbf24' },
          { label: 'Converted to User', value: data.converted, icon: ArrowRight, color: '#f472b6',
            sub: data.unique_guests > 0 ? `${((data.converted / data.unique_guests) * 100).toFixed(1)}% rate` : '—' },
        ].map((s, i) => (
          <div key={i} className="rounded-xl p-3" style={{ background: statBg }} data-testid={`guest-stat-${i}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <s.icon className="h-3 w-3" style={{ color: s.color }} />
              <span className="text-[10px]" style={{ color: colors.textMuted }}>{s.label}</span>
            </div>
            <p className="text-lg font-bold" style={{ color: colors.text }}>{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</p>
            {s.sub && <p className="text-[10px] mt-0.5" style={{ color: s.color }}>{s.sub}</p>}
          </div>
        ))}
      </div>

      {/* Countries + Popular Queries side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Countries */}
        <div className="rounded-xl p-4" style={{ background: statBg }} data-testid="guest-countries">
          <div className="flex items-center gap-1.5 mb-3">
            <Globe className="h-3.5 w-3.5" style={{ color: colors.accent }} />
            <span className="text-xs font-medium" style={{ color: colors.text }}>Top Countries</span>
          </div>
          {data.countries.length > 0 ? (
            <div className="space-y-2">
              {data.countries.slice(0, 8).map((c, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{c.code ? getFlagEmoji(c.code) : '🌍'}</span>
                    <span className="text-xs" style={{ color: colors.text }}>{c.country || 'Unknown'}</span>
                  </div>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.1)', color: '#818cf8' }}>
                    {c.count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs" style={{ color: colors.textMuted }}>No country data yet. New guests will show here.</p>
          )}
        </div>

        {/* Popular queries */}
        <div className="rounded-xl p-4" style={{ background: statBg }} data-testid="guest-queries">
          <div className="flex items-center gap-1.5 mb-3">
            <MessageSquare className="h-3.5 w-3.5" style={{ color: colors.accent }} />
            <span className="text-xs font-medium" style={{ color: colors.text }}>Popular Queries</span>
          </div>
          {data.popular_queries?.length > 0 ? (
            <div className="space-y-2">
              {data.popular_queries.slice(0, 6).map((q, i) => (
                <div key={i} className="flex items-center gap-2">
                  <ArrowRight className="h-2.5 w-2.5 shrink-0" style={{ color: colors.textMuted }} />
                  <span className="text-xs truncate" style={{ color: colors.text }}>{q.query}</span>
                  <span className="text-[10px] shrink-0 ml-auto" style={{ color: colors.textMuted }}>{q.messages} msgs</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs" style={{ color: colors.textMuted }}>No guest queries yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function getFlagEmoji(countryCode) {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const code = countryCode.toUpperCase();
  return String.fromCodePoint(...[...code].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}
